// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CheckCircle as CheckCircleIcon,
  CheckRounded,
  ContentCopyRounded,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@/constants/hardfin-standard-icons';
import { TimeDataUploadResult } from '@/costs/api/upload-time-data';
import type { ExpandedTimeState } from '@/costs/components/time-data-upload/types';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import { formatFailedRowsForCopy } from '@/utilities/upload-helpers';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { FixedSizeList as VirtualList } from 'react-window';

const ITEM_HEIGHT = 40;
const FAILED_ROW_HEIGHT = 80;

interface ResultsProps {
  uploadResult: TimeDataUploadResult | null;
  expandedTime: ExpandedTimeState;
  onToggleExpanded: (section: keyof ExpandedTimeState) => void;
  onBack: () => void;
  onStartNew: () => void;
  onClose: () => void;
}

interface RowFailure {
  rowNumber: number;
  workerName: string;
  category: string;
  errors: string[];
}

type TimeRecordItem = TimeDataUploadResult['items'][number];

interface TimeRecordRowProps {
  index: number;
  style: React.CSSProperties;
  data: TimeRecordItem[];
}

interface FailedRowItemProps {
  index: number;
  style: React.CSSProperties;
  data: RowFailure[];
}

function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours === 0) {
    return `${minutes}m`;
  } else if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}m`;
}

const TimeRecordRow = ({ index, style, data }: TimeRecordRowProps) => {
  const timeRecord = data[index];
  return (
    <div style={style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={
            <Typography variant="body2">
              <strong>{timeRecord.workerName}</strong> -{' '}
              <strong>{formatMinutes(timeRecord.totalMinutes)}</strong> of{' '}
              <strong>{timeRecord.category}</strong> on{' '}
              <strong>{timeRecord.date}</strong>
            </Typography>
          }
        />
      </ListItem>
    </div>
  );
};

const FailedRowItem = ({ index, style, data }: FailedRowItemProps) => {
  const row = data[index];
  return (
    <div style={style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={
            <Typography variant="body2" color="error">
              <strong>Row {row.rowNumber}:</strong>{' '}
              {row.workerName && (
                <>
                  {row.workerName} - {row.category}
                </>
              )}
            </Typography>
          }
          secondary={row.errors.join(', ')}
        />
      </ListItem>
    </div>
  );
};

export function Results(props: ResultsProps) {
  const [isCopied, setIsCopied] = useState(false);

  if (!props.uploadResult) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            No upload results available. Please try again.
          </Typography>
          <Button variant="contained" onClick={props.onStartNew} sx={{ mt: 2 }}>
            Start new upload
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { success, items, structureErrors, rowErrors } = props.uploadResult;

  const timeRecordsCreated = items.filter(
    (item) => !item.errors || item.errors.length === 0
  );
  const failedRows: RowFailure[] = [];

  /**
   * Handles copying all failed row errors to clipboard with visual feedback
   */
  const handleCopyErrors = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle when clicking copy
    try {
      const text = formatFailedRowsForCopy(failedRows, (row) => {
        if (row.workerName && row.category) {
          return `(${row.workerName} - ${row.category})`;
        }
        if (row.workerName) {
          return `(${row.workerName})`;
        }
        return undefined;
      });
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy errors:', error);
    }
  };

  // Process row errors (validation errors)
  rowErrors.forEach((row) => {
    if (row.errors.length > 0) {
      failedRows.push({
        rowNumber: row.index,
        workerName: '',
        category: '',
        errors: row.errors.map((err) => `${err.fieldName}: ${err.error}`),
      });
    }
  });

  // Process item errors (individual time record errors)
  items.forEach((item) => {
    if (item.errors && item.errors.length > 0) {
      failedRows.push({
        rowNumber: item.index,
        workerName: item.workerName || '',
        category: item.category || '',
        errors: item.errors.map((err) => `${err.fieldName}: ${err.error}`),
      });
    }
  });

  const totalProcessed = timeRecordsCreated.length;
  const hasErrors = failedRows.length > 0 || structureErrors.length > 0;

  return (
    <Card>
      <CardContent>
        {success && !hasErrors ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <AlertTitle>Success</AlertTitle>
              <Typography variant="body2">
                Your time data uploaded successfully for{' '}
                {pluralize(totalProcessed, {
                  singular: 'time record',
                  plural: 'time records',
                })}
              </Typography>
            </Alert>

            {timeRecordsCreated.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                    cursor: timeRecordsCreated.length ? 'pointer' : undefined,
                  }}
                  onClick={
                    timeRecordsCreated.length
                      ? () => props.onToggleExpanded('created')
                      : undefined
                  }
                >
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Time records created (
                      {formatNumber(timeRecordsCreated.length)})
                    </Typography>
                  </Box>
                  {timeRecordsCreated.length > 0 &&
                    (props.expandedTime.created ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    ))}
                </Box>
                <Collapse in={props.expandedTime.created}>
                  <VirtualList
                    height={Math.min(
                      timeRecordsCreated.length * ITEM_HEIGHT,
                      200
                    )}
                    itemCount={timeRecordsCreated.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={timeRecordsCreated}
                    width="100%"
                  >
                    {TimeRecordRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {structureErrors.length > 0 && (
                <>
                  <AlertTitle>File format invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file has column names that do not match the
                    required time data fields
                  </Typography>
                </>
              )}
              {failedRows.length > 0 && (
                <>
                  <AlertTitle>Uploaded data invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file includes data that does not match required
                    fields
                  </Typography>
                </>
              )}
            </Alert>

            {/* Show time records created */}
            {timeRecordsCreated.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('created')}
                >
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Ok rows ({formatNumber(timeRecordsCreated.length)})
                    </Typography>
                  </Box>
                  {props.expandedTime.created ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={props.expandedTime.created}>
                  <VirtualList
                    height={Math.min(
                      timeRecordsCreated.length * ITEM_HEIGHT,
                      200
                    )}
                    itemCount={timeRecordsCreated.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={timeRecordsCreated}
                    width="100%"
                  >
                    {TimeRecordRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}

            {/* Show structure errors */}
            {structureErrors.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('structureErrors')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Structure errors ({formatNumber(structureErrors.length)})
                    </Typography>
                  </Box>
                  {props.expandedTime.structureErrors ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={props.expandedTime.structureErrors}>
                  <ul
                    style={{
                      maxHeight: '200px',
                      overflow: 'auto',
                      listStyleType: 'disc',
                    }}
                  >
                    {structureErrors.map((error, index) => (
                      <li
                        key={`structure-${index}`}
                        style={{ paddingBottom: '8px' }}
                      >
                        <strong>{error.fieldName}:</strong> {error.error}
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </Paper>
            )}

            {/* Show failed rows */}
            {failedRows.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('failed')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Failed rows ({formatNumber(failedRows.length)})
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip
                      title={isCopied ? 'Copied!' : 'Copy all errors'}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={handleCopyErrors}
                        aria-label={isCopied ? 'Copied' : 'Copy all errors'}
                        sx={{
                          color: 'error.contrastText',
                          '&:hover': {
                            bgcolor: 'error.dark',
                          },
                        }}
                      >
                        {isCopied ? (
                          <CheckRounded fontSize="small" />
                        ) : (
                          <ContentCopyRounded fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {props.expandedTime.failed ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </Box>
                </Box>
                <Collapse in={props.expandedTime.failed}>
                  <VirtualList
                    height={Math.min(
                      failedRows.length * FAILED_ROW_HEIGHT,
                      200
                    )}
                    itemCount={failedRows.length}
                    itemSize={FAILED_ROW_HEIGHT}
                    itemData={failedRows}
                    width="100%"
                  >
                    {FailedRowItem}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        )}

        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button variant="outlined" onClick={props.onBack}>
            Back
          </Button>
          <Button variant="contained" onClick={props.onStartNew}>
            Upload more time data
          </Button>
          <Button variant="contained" color="primary" onClick={props.onClose}>
            Close
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
